import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { FormPaper } from "src/apps/commons/papers";
import * as Api from "../api";
import { IRemyndProduct, IRemyndProductSaveReq } from "../types";
import { constants, paths } from "../utils";
import Form from "./Form";

interface Props {
  id?: RemyndProductId;
}

interface StateTypes {
  fetching: boolean;
  data?: IRemyndProduct;
}

interface ConfirmDialogStateTypes {
  open: boolean;
  onConfirm?: () => void;
}

const FormContainers: React.FC<Props> = props => {
  const { id } = props;
  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateTypes>({ fetching: true });
  const { fetching, data } = state;

  const [saving, setSaving] = useState(false);
  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };
  const handleSave = useCallback(
    (req: IRemyndProductSaveReq) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            if (id) {
              await Api.update(id, req);
            } else {
              await Api.save(req);
            }
            await router.push(paths.index);
            setMessage(
              "success",
              `${id ? "แก้ไข" : "สร้าง"} ${constants.label} "${
                req.nameTh
              }" สำเร็จ`
            );
          } catch (e) {
            handleError(e);
            setSaving(false);
            handleCloseDialog();
          }
        }
      });
    },
    [setMessage, handleError, router, id]
  );

  const [deleting, setDeleting] = useState(false);
  const [deleteDialogState, setDeleteDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDeleteDialog = () => {
    setDeleteDialogState({ open: false, onConfirm: undefined });
  };
  const handleDelete = useCallback(() => {
    if (!id) return;
    setDeleteDialogState({
      open: true,
      onConfirm: async () => {
        try {
          setDeleting(true);
          await Api.deleteMultiple({ ids: [id] });
          await router.push(paths.index);
          setMessage("success", `ลบ ${constants.label} สำเร็จ`);
        } catch (e) {
          handleError(e);
          setDeleting(false);
          handleCloseDeleteDialog();
        }
      }
    });
  }, [setMessage, handleError, router, id]);

  const fetchProduct = useCallback(
    async (remyndProductId: RemyndProductId) => {
      setState(d => ({ ...d, fetching: true }));
      try {
        const resp = await Api.get(remyndProductId);
        setState({ fetching: false, data: resp.value });
      } catch (e) {
        handleError(e);
        setState({ fetching: false, data: undefined });
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (id) {
      fetchProduct(id);
    } else {
      setState({ fetching: false, data: undefined });
    }
  }, [id, fetchProduct]);

  return (
    <FormPaper>
      <Form
        isLoading={fetching}
        isSaving={saving}
        data={data}
        onSubmit={handleSave}
        onDelete={id ? handleDelete : undefined}
      />

      <ConfirmDialog
        title={`ยืนยันการ${id ? "แก้ไข" : "สร้าง"}${constants.label}?`}
        open={confirmDialogState.open}
        onClose={handleCloseDialog}
        onConfirm={confirmDialogState.onConfirm}
        confirming={saving}
      />
      <ConfirmDialog
        title={`ยืนยันการลบ${constants.label}?`}
        open={deleteDialogState.open}
        onClose={handleCloseDeleteDialog}
        onConfirm={deleteDialogState.onConfirm}
        confirming={deleting}
      />
    </FormPaper>
  );
};

export default FormContainers;
